import React from "react";
import { Card, CardBody, Button, Badge, Input, Spinner } from "reactstrap";
import { parseDateInput, toISODate } from "../../../../utils/date";

const WorkoutSidebar = ({
    workouts,
    activeId,
    onSelect,
    onCreateNew,
    loading,
    selectedDate,
    onDateChange
}) => {
    const handlePrevDay = () => {
        const d = parseDateInput(selectedDate);
        d.setDate(d.getDate() - 1);
        onDateChange(toISODate(d));
    };

    const handleNextDay = () => {
        const d = parseDateInput(selectedDate);
        d.setDate(d.getDate() + 1);
        onDateChange(toISODate(d));
    };

    const handleToday = () => {
        onDateChange(toISODate(new Date()));
    };

    const calculateVolume = (blocks) => {
        let total = 0;
        blocks?.forEach(block => {
            block.sets?.forEach(set => {
                total += (Number(set.reps) || 0) * (Number(set.distance) || 0);
            });
        });
        return total;
    };

    return (
        <Card className="border-0 shadow-sm h-100 overflow-hidden d-flex flex-column rounded-3" style={{ minHeight: '600px' }}>
            <div className="bg-white p-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-start mb-3">
                    <div className="flex-shrink-0 me-2 text-primary">
                        <i className="mdi mdi-calendar-clock font-size-22"></i>
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold">Treinos do Dia</h6>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between bg-light rounded-3 p-1 mb-3 border">
                    <Button color="light" size="sm" className="border-0 bg-transparent text-secondary shadow-none px-2" onClick={handlePrevDay}>
                        <i className="mdi mdi-chevron-left font-size-18"></i>
                    </Button>
                    <div className="d-flex flex-column align-items-center" style={{ flexGrow: 1 }}>
                        <span className="font-size-11 fw-bold text-primary text-uppercase cursor-pointer mb-1" onClick={handleToday} style={{ letterSpacing: '0.5px' }}>
                            Hoje
                        </span>
                        <Input
                            type="date"
                            className="form-control-sm border-0 bg-transparent fw-bold text-center text-dark p-0"
                            style={{ width: '110px', boxShadow: 'none' }}
                            value={selectedDate}
                            onChange={(e) => onDateChange(e.target.value)}
                        />
                    </div>
                    <Button color="light" size="sm" className="border-0 bg-transparent text-secondary shadow-none px-2" onClick={handleNextDay}>
                        <i className="mdi mdi-chevron-right font-size-18"></i>
                    </Button>
                </div>

                <Button
                    color="primary"
                    className="w-100 border-0 fw-bold shadow-sm rounded-3 py-2 d-flex align-items-center justify-content-center"
                    onClick={onCreateNew}
                >
                    <i className="mdi mdi-plus-circle-outline font-size-16 me-2"></i>
                    Novo Treino
                </Button>
            </div>

            <div className="flex-grow-1 overflow-auto bg-light-subtle p-2">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" size="sm" />
                        <p className="text-muted small mt-2">Carregando...</p>
                    </div>
                ) : workouts.length === 0 ? (
                    <div className="text-center p-4">
                        <div className="avatar-md mx-auto mb-3">
                            <div className="avatar-title bg-white text-muted rounded-circle shadow-sm border">
                                <i className="mdi mdi-pool font-size-24"></i>
                            </div>
                        </div>
                        <p className="text-muted font-size-13">Nenhum treino planejado para esta data.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {workouts.map((workout) => (
                            <div
                                key={workout.id}
                                className={`card mb-0 cursor-pointer border rounded-3 transition-all ${activeId === workout.id ? 'border-primary shadow-sm ring-1 ring-primary' : 'border-light shadow-none hover-border-primary'}`}
                                onClick={() => onSelect(workout)}
                                style={{
                                    backgroundColor: activeId === workout.id ? '#f3f8ff' : '#fff'
                                }}
                            >
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="font-size-14 fw-bold mb-0 text-dark">
                                            {workout.title}
                                        </h6>
                                    </div>

                                    {/* Exibição completa das séries do Treino */}
                                    <div className="mt-3 mb-2">
                                        {workout.blocks?.map((block, idx) => (
                                            <div key={idx} className="mb-3 last-mb-0">
                                                <div className="font-size-11 fw-bold text-uppercase text-muted border-bottom pb-1 mb-2 d-flex align-items-center">
                                                    <i className="mdi mdi-bookmark-outline me-1"></i> {block.name}
                                                </div>
                                                {block.sets?.length > 0 ? (
                                                    <div className="ps-2 d-flex flex-column gap-1">
                                                        {block.sets.map((set, sIdx) => (
                                                            <div key={sIdx} className="font-size-12 text-dark d-flex align-items-center">
                                                                <span className="fw-bold pe-2" style={{ minWidth: '55px' }}>{set.reps}x{set.distance}m</span>
                                                                <span className="text-truncate d-flex align-items-center">
                                                                    {set.style && <span className="text-primary me-2">{set.style}</span>}
                                                                    {set.intensity && <Badge color="soft-danger" pill className="font-size-9 px-1 me-2">{set.intensity}</Badge>}
                                                                    {set.description && <span className="text-muted fst-italic">- {set.description}</span>}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="font-size-11 text-muted fst-italic ps-2">Nenhuma série.</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="d-flex justify-content-end mt-3 border-top pt-3">
                                        <div className="text-muted fw-bold font-size-12 bg-light px-2 py-1 rounded shadow-sm border border-light">
                                            <i className="mdi mdi-ruler me-1 text-primary"></i>
                                            {calculateVolume(workout.blocks)}m
                                        </div>
                                    </div>
                                </CardBody>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default WorkoutSidebar;
